/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Tab, Tabs, Typography } from '@mui/material'
import React from 'react'
import { useLanguageLocale } from '../../../../hooks/language-locale-hooks';
import * as PXBColors from '@brightlayer-ui/colors';
import TestGroupTable from './TestGroupTable';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { WhitelistZoneData } from '../../../../state/assets-state';
import { useSelector } from 'react-redux';
import { Selectors } from '../../../../selectors';

type Props = {
    deviceWhitelistZone: WhitelistZoneData | null,
    searchQuery: string,
    setSearchQuery: (query: string) => void,
    rowsPerPage: number,
    setRowsPerPage: (rowsPerPage: number) => void,
    offset: number,
    setOffset: (offset: number) => void,
    currentPage: number,
    setCurrentPage: (page: number) => void,
    testGroupTabIndex: number,
    setTestGroupTabIndex: (index: number) => void,
    totalLuminairesCount: number,
    setTotalLuminairesCount: (count: number) => void,
    groupCounts: { group1: number, group2: number, group3: number, group4: number },
    setGroupCounts: (counts: { group1: number, group2: number, group3: number, group4: number }) => void
}
const TEST_GROUP_TAB_KEY = 'testGroupTabState'; // Changed from 'backState' to avoid conflict
const TAB_ALL = 0;
const TAB_ALL_KEY = "All";
const TAB_GROUP1 = 1;
const TAB_GROUP1_KEY = "Group 1";
const TAB_GROUP2 = 2;
const TAB_GROUP2_KEY = "Group 2";
const TAB_GROUP3 = 3;
const TAB_GROUP3_KEY = "Group 3";
const TAB_GROUP4 = 4;
const TAB_GROUP4_KEY = "Group 4";

const getTabState = (): number => {
    const tabState = sessionStorage.getItem(TEST_GROUP_TAB_KEY);
    switch (tabState) {
        case TAB_ALL_KEY:
            return TAB_ALL;
        case TAB_GROUP1_KEY:
            return TAB_GROUP1;
        case TAB_GROUP2_KEY:
            return TAB_GROUP2;
        case TAB_GROUP3_KEY:
            return TAB_GROUP3;
        case TAB_GROUP4_KEY:
            return TAB_GROUP4;
        default:
            return TAB_ALL;
    }
}

const updateTabSession = (tab: string): void => {
    sessionStorage.setItem(TEST_GROUP_TAB_KEY, tab);
};

const TestSchedule = (props: Props) => {
    const { t } = useLanguageLocale();
    const asset = useSelector(Selectors.Asset.assets);
    const [openCommentDialog, setOpenCommentDialog] = React.useState(false);
    const hasInitialized = React.useRef(false);

    // Sync parent's tab index on mount from session storage - only once
    React.useEffect(() => {
        if (!hasInitialized.current) {
            const savedTabIndex = getTabState();
            props.setTestGroupTabIndex(savedTabIndex);
            hasInitialized.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Get all luminaires from Redux
    const allLuminaires = asset.configureLuminariesListDetails?.luminaryConfigs || [];

    // Store initial total count and group counts in parent state when on "All" tab and not yet stored
    React.useEffect(() => {
        if (props.testGroupTabIndex === 0 && allLuminaires.length > 0 && props.totalLuminairesCount === 0) {
            props.setTotalLuminairesCount(allLuminaires.length);

            // Calculate and store initial group counts
            const g1Count = allLuminaires.filter(lum => lum.testGroupId === 1).length;
            const g2Count = allLuminaires.filter(lum => lum.testGroupId === 2).length;
            const g3Count = allLuminaires.filter(lum => lum.testGroupId === 3).length;
            const g4Count = allLuminaires.filter(lum => lum.testGroupId === 4).length;

            props.setGroupCounts({
                group1: g1Count,
                group2: g2Count,
                group3: g3Count,
                group4: g4Count
            });
        }
    }, [props.testGroupTabIndex, props.totalLuminairesCount, props.setTotalLuminairesCount, props.setGroupCounts, allLuminaires]);

    // Use stored counts from parent (never change after first capture)
    const displayTotalCount = props.totalLuminairesCount || allLuminaires.length;
    const group1Count = props.groupCounts.group1 || allLuminaires.filter(lum => lum.testGroupId === 1).length;
    const group2Count = props.groupCounts.group2 || allLuminaires.filter(lum => lum.testGroupId === 2).length;
    const group3Count = props.groupCounts.group3 || allLuminaires.filter(lum => lum.testGroupId === 3).length;
    const group4Count = props.groupCounts.group4 || allLuminaires.filter(lum => lum.testGroupId === 4).length;

    function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
        const { children, value, index, ...other } = props;
        return <div {...other}>{value === index && <Box p={3}>{children}</Box>}</div>;
    }

    const handleTabChange = (_event: any, newValue: React.SetStateAction<number>) => {
        const newIndex = newValue as number;

        // Update session storage based on the new tab
        const tabKeys = [TAB_ALL_KEY, TAB_GROUP1_KEY, TAB_GROUP2_KEY, TAB_GROUP3_KEY, TAB_GROUP4_KEY];
        updateTabSession(tabKeys[newIndex]);

        // Send tab index to parent WhiteListTabs
        props.setTestGroupTabIndex(newIndex);
    };

    const handleCloseCommentDialog = (): void => {
        setOpenCommentDialog(false);
    }

    const onCommentsSave = () => {
        setOpenCommentDialog(false);
    }

    const handleResetLumnGroup = (): void => {
        setOpenCommentDialog(true);
    }

    const handleSearchChange = (query: string): void => {
        props.setSearchQuery(query);
    }


    return (
        <>
            <div data-testid="test-group-container">
                <Box>
                    {/* <Typography data-testid="test-group-description">
                        {t('ASSETS_DETAILS.TEST_GROUP_DESC')}
                    </Typography>
                    <Typography data-testid='test-group-info' sx={{ display: 'flex', gap: '1%', marginTop: '2%' }} fontWeight={400}>
                        <InfoOutlinedIcon /> {t('ASSETS_DETAILS.TEST_GROUP_INFO')}
                    </Typography>
                    <Button disabled data-testid="reset-lumn-groups-button" variant="outlined" sx={{ marginTop: '1%', borderColor: "red" }} color="error" onClick={handleResetLumnGroup} startIcon={<RestartAltIcon />}>
                        {t('ASSETS_DETAILS.RESET_LUMN_GROUPS')}
                    </Button> */}
                    <Box sx={{ marginTop: 2, boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.3)' }}>
                        <Tabs
                            indicatorColor='secondary' variant="scrollable"
                            scrollButtons={false}
                            value={props.testGroupTabIndex} onChange={handleTabChange} sx={{
                                backgroundColor: PXBColors.white[50],
                                '& .MuiTab-root': {
                                    color: 'black !important',
                                    textTransform: 'none',
                                    marginLeft: '35px',
                                    '&.Mui-selected': {
                                        color: `${PXBColors.blue[500]} !important`,
                                    }
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: PXBColors.blue[500],
                                    height: '3px',
                                }
                            }}>
                            <Tab data-testid="tabsAll" color="Black" label={`${t('ORGANIZATION_MANAGE.ALL')} (${displayTotalCount})`} />
                            <Tab data-testid="tabsGroup1" sx={{ color: "black" }} label={`${t('ASSETS_DETAILS.GROUP_TAB')} 1 (${group1Count})`} />
                            <Tab data-testid="tabsGroup2" sx={{ color: "black" }} label={`${t('ASSETS_DETAILS.GROUP_TAB')} 2 (${group2Count})`} />
                            <Tab data-testid="tabsGroup3" sx={{ color: "black" }} label={`${t('ASSETS_DETAILS.GROUP_TAB')} 3 (${group3Count})`} />
                            <Tab data-testid="tabsGroup4" sx={{ color: "black" }} label={`${t('ASSETS_DETAILS.GROUP_TAB')} 4 (${group4Count})`} />
                        </Tabs>
                        <Divider style={{ color: 'grey' }} />
                        <TabPanel value={props.testGroupTabIndex} index={TAB_ALL} data-testid="tab-all-container">
                            <TestGroupTable deviceWhitelistZone={props.deviceWhitelistZone} currentLumnCount={displayTotalCount} othersLumnCount={0} testGroupId={null} searchQuery={props.searchQuery} onSearchChange={handleSearchChange} rowsPerPage={props.rowsPerPage} setRowsPerPage={props.setRowsPerPage} offset={props.offset} setOffset={props.setOffset} currentPage={props.currentPage} setCurrentPage={props.setCurrentPage} />
                        </TabPanel>
                        <TabPanel value={props.testGroupTabIndex} index={TAB_GROUP1} data-testid="tab-group1-container">
                            <TestGroupTable deviceWhitelistZone={props.deviceWhitelistZone} currentLumnCount={group1Count} othersLumnCount={displayTotalCount - group1Count} testGroupId={1} searchQuery={props.searchQuery} onSearchChange={handleSearchChange} rowsPerPage={props.rowsPerPage} setRowsPerPage={props.setRowsPerPage} offset={props.offset} setOffset={props.setOffset} currentPage={props.currentPage} setCurrentPage={props.setCurrentPage} />
                        </TabPanel>
                        <TabPanel value={props.testGroupTabIndex} index={TAB_GROUP2} data-testid="tab-group2-container">
                            <TestGroupTable deviceWhitelistZone={props.deviceWhitelistZone} currentLumnCount={group2Count} othersLumnCount={displayTotalCount - group2Count} testGroupId={2} searchQuery={props.searchQuery} onSearchChange={handleSearchChange} rowsPerPage={props.rowsPerPage} setRowsPerPage={props.setRowsPerPage} offset={props.offset} setOffset={props.setOffset} currentPage={props.currentPage} setCurrentPage={props.setCurrentPage} />
                        </TabPanel>
                        <TabPanel value={props.testGroupTabIndex} index={TAB_GROUP3} data-testid="tab-group3-container">
                            <TestGroupTable deviceWhitelistZone={props.deviceWhitelistZone} currentLumnCount={group3Count} othersLumnCount={displayTotalCount - group3Count} testGroupId={3} searchQuery={props.searchQuery} onSearchChange={handleSearchChange} rowsPerPage={props.rowsPerPage} setRowsPerPage={props.setRowsPerPage} offset={props.offset} setOffset={props.setOffset} currentPage={props.currentPage} setCurrentPage={props.setCurrentPage} />
                        </TabPanel>
                        <TabPanel value={props.testGroupTabIndex} index={TAB_GROUP4} data-testid="tab-group4-container">
                            <TestGroupTable deviceWhitelistZone={props.deviceWhitelistZone} currentLumnCount={group4Count} othersLumnCount={displayTotalCount - group4Count} testGroupId={4} searchQuery={props.searchQuery} onSearchChange={handleSearchChange} rowsPerPage={props.rowsPerPage} setRowsPerPage={props.setRowsPerPage} offset={props.offset} setOffset={props.setOffset} currentPage={props.currentPage} setCurrentPage={props.setCurrentPage} />
                        </TabPanel>
                    </Box>
                </Box>
            </div>

            <Dialog id='commentDialogBox' fullWidth open={openCommentDialog} onClose={handleCloseCommentDialog} style={{ borderRadius: 4, boxShadow: '0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px rgba(0, 0, 0, 0.14), 0px 1px 18px rgba(0, 0, 0, 0.12)' }} >
                <DialogTitle>
                    {t('ASSETS_DETAILS.REASSIGN_TEST_GROUPS')}
                </DialogTitle>
                <DialogContent id='commentBoxContent' style={{ paddingLeft: 23, paddingRight: 20, paddingBottom: 10 }}>
                    <Typography fontSize={16}>
                        {t('ASSETS_DETAILS.REASSIGN_TEST_GROUPS_CONFIRM')}
                    </Typography>
                </DialogContent>
                <Divider style={{ color: 'grey' }} />
                <DialogActions>
                    <Button
                        style={{
                            marginBottom: 10,
                            width: 'auto',
                            height: 36,
                            flexDirection: 'row',
                            marginRight: 10,
                            backgroundColor: PXBColors.white[50],
                        }}
                        id="cancelButton"
                        variant="outlined"
                        color="primary"
                        data-testid="cancelButton"
                        onClick={handleCloseCommentDialog}
                        startIcon={<CloseIcon />}
                    >
                        {t('ACTIONS.CANCEL')}
                    </Button>
                    <Button
                        style={{
                            marginBottom: 10,
                            width: 'auto',
                            height: 36,
                            flexDirection: 'row',
                            backgroundColor: PXBColors.red[500],
                            color: PXBColors.white[50],
                        }}
                        startIcon={<CheckIcon />}
                        color="error"
                        id="saveButton"
                        data-testid="saveButton"
                        variant='contained'
                        onClick={onCommentsSave}
                    >
                        {t('ACTIONS.CONFIRM')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default TestSchedule
